import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import { useIdleLogout } from '../hooks/useIdleLogout';

const AuthContext = createContext(null);

const DEFAULT_IDLE = { idle_timeout_minutes: 3, idle_warning_seconds: 30 };

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('mpms_user');
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(false);
  const [idleSettings, setIdleSettings] = useState(DEFAULT_IDLE);

  const login = useCallback(async (username, password) => {
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { username, password });
      localStorage.setItem('mpms_token', res.data.token);
      localStorage.setItem('mpms_user', JSON.stringify(res.data.user));
      setUser(res.data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.response?.data?.detail || 'Login failed' };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    // SEC-002 · P1: revoke the session server-side (bumps token_version_at so
    // every previously-issued token for this user is refused on the next
    // request). Fire-and-forget: even if the API call fails (offline / expired
    // token) we still tear down the client session.
    try {
      await api.post('/auth/logout');
    } catch (e) {
      console.warn('Server logout failed (continuing with client-side sign-out):', e?.message || e);
    }
    localStorage.removeItem('mpms_token');
    localStorage.removeItem('mpms_user');
    setUser(null);
    window.location.href = '/login';
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const r = await api.get('/auth/me');
      setUser(r.data);
      localStorage.setItem('mpms_user', JSON.stringify(r.data));
    } catch (e) {}
  }, []);

  const reloadIdleSettings = useCallback(async () => {
    try {
      const r = await api.get('/settings/security');
      const m = Number(r.data?.idle_timeout_minutes);
      const w = Number(r.data?.idle_warning_seconds);
      if (Number.isFinite(m) && m > 0) {
        setIdleSettings({
          idle_timeout_minutes: m,
          idle_warning_seconds: Number.isFinite(w) && w > 0 ? w : 30,
        });
      }
    } catch (e) { /* keep defaults */ }
  }, []);

  useEffect(() => {
    if (localStorage.getItem('mpms_token') && !user) refreshMe();
    // eslint-disable-next-line
  }, []);

  // Re-fetch idle settings whenever a user session is (re)established.
  useEffect(() => {
    if (user) reloadIdleSettings();
  }, [user, reloadIdleSettings]);

  // Auto-logout after the configured idle timeout. Hook is a no-op when no user.
  useIdleLogout({
    enabled: !!user,
    onLogout: logout,
    timeoutMs: idleSettings.idle_timeout_minutes * 60 * 1000,
    warningMs: idleSettings.idle_warning_seconds * 1000,
  });

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, refreshMe, idleSettings, reloadIdleSettings }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export const hasRole = (user, ...roles) => user && roles.includes(user.role);
